import { Box, Button, Dialog, Divider, Stack } from "@mui/material";
import DialogHeader from "@entrilia/shared/components/dialog/DialogHeader";
import type { CurrencyDimensionMeta, CustomColumnField } from "../../../../../../../../../api/types/svTypes";
import type { CustomColumnData } from "./customColumn.types";
import CustomFormulaColumnsPanel from "./CustomFormulaColumnsPanel";
import CustomFormulaDefinitionPanel from "./CustomFormulaDefinitionPanel";
import CustomFormulaEditorPanel from "./CustomFormulaEditorPanel";
import CustomFormulaFunctionsPanel from "./CustomFormulaFunctionsPanel";
import useCustomFormulaDialogForm from "./hooks/useCustomFormulaDialogForm";
import type { FormulaReferenceField } from "./utilities/customFormulaParser";

interface Props {
  open: boolean;
  onClose: () => void;
  onApply: (data: CustomColumnData) => void;
  editedField?: CustomColumnField;
  sourceFieldGuid?: string;
  referableFields?: FormulaReferenceField[];
  referableFieldsTitle?: string;
  allowGrandTotal?: boolean;
  currencyDimensions?: CurrencyDimensionMeta[];
  getReferenceError?: (formulaParameters: string[]) => string | undefined;
}

const CustomFormulaDialog = ({
  open,
  onClose,
  onApply,
  editedField,
  sourceFieldGuid,
  referableFields,
  referableFieldsTitle,
  allowGrandTotal,
  currencyDimensions,
  getReferenceError,
}: Props) => {
  const {
    name,
    nameError,
    formulaInput,
    formulaError,
    format,
    blankIfMissingValue,
    filteredFieldsForCalculation,
    formulaReferenceFields,
    formulaFunctions,
    formulaFunctionNames,
    formulaInputRef,
    isApplyDisabled,
    handleNameChange,
    handleNameBlur,
    handleSearchChange,
    handleFormatChange,
    handleFormulaChange,
    handleFormulaBlur,
    handleFieldSelect,
    handleFunctionSelect,
    handleBlankIfMissingValueChange,
    handleApply,
  } = useCustomFormulaDialogForm({
    editedField,
    sourceFieldGuid,
    referableFields,
    allowGrandTotal,
    getReferenceError,
    onApply,
  });

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        onClose();
      }}
      fullWidth
      maxWidth="lg"
      slotProps={{ paper: { sx: { height: 760, maxHeight: "95vh", borderRadius: "8px" } } }}
    >
      <Stack sx={{ height: "100%" }}>
        <DialogHeader text="Custom Formula" onClose={onClose} />
        <Divider />
        <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
          <Stack sx={(theme) => ({ width: 320, borderRight: `1px solid ${theme.palette.divider}` })}>
            <CustomFormulaDefinitionPanel
              name={name}
              nameError={nameError}
              format={format}
              blankIfMissingValue={blankIfMissingValue}
              currencyDimensions={currencyDimensions}
              onNameChange={handleNameChange}
              onNameBlur={handleNameBlur}
              onFormatChange={handleFormatChange}
              onBlankIfMissingValueChange={handleBlankIfMissingValueChange}
            />
            <Divider />
            <CustomFormulaColumnsPanel
              title={referableFieldsTitle}
              fields={filteredFieldsForCalculation}
              onSearchChange={handleSearchChange}
              onFieldSelect={handleFieldSelect}
            />
          </Stack>
          <Stack sx={{ flex: 1, minHeight: 0 }}>
            <CustomFormulaEditorPanel
              formulaInput={formulaInput}
              formulaError={formulaError}
              referenceFields={formulaReferenceFields}
              functions={formulaFunctionNames}
              inputRef={formulaInputRef}
              onFormulaChange={handleFormulaChange}
              onFormulaBlur={handleFormulaBlur}
            />
            <Stack sx={{ px: 2.5, pb: 2.5, minHeight: 0 }}>
              <CustomFormulaFunctionsPanel functions={formulaFunctions} onFunctionSelect={handleFunctionSelect} />
            </Stack>
          </Stack>
        </Box>
        <Divider />
        <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end", px: 2.5, py: 1.5 }}>
          <Button variant="text" color="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleApply} disabled={isApplyDisabled}>
            Apply
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  );
};

export default CustomFormulaDialog;
